import {DifficultySummaries, QUESTION_DIFFICULTIES, completionScoreBarColor} from '@domain/pom'
import {completionScoreBandColor} from '@hconnect/common/utils'
import {Grid, Popover, Typography} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {POM_COMPLETION_WIDGET_NAMESPACE} from '../consts'

type RatingTooltipProps = {
  children: React.ReactNode
  value: number
  summaries: DifficultySummaries
  translationPrefix?: string
  hideTotal?: boolean
}

export const RatingTooltip: React.FC<RatingTooltipProps> = ({
  children,
  value,
  summaries,
  hideTotal
}) => {
  const {t} = useTranslation(POM_COMPLETION_WIDGET_NAMESPACE)

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const totalPositive = useMemo(
    () =>
      QUESTION_DIFFICULTIES.reduce((acc, difficulty) => {
        acc += summaries[difficulty].answeredPositively
        return acc
      }, 0),
    [summaries]
  )

  const totalQuestions = useMemo(
    () =>
      QUESTION_DIFFICULTIES.reduce((acc, difficulty) => {
        acc += summaries[difficulty].questionCount
        return acc
      }, 0),
    [summaries]
  )

  const open = Boolean(anchorEl)

  return (
    <Typography
      aria-owns={open ? 'mouse-over-popover' : undefined}
      aria-haspopup="true"
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      component="div"
    >
      {children}
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none'
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'common.black',
            p: 2
          }
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        data-test-id="rating-popover"
      >
        <Grid container sx={{color: 'common.white'}} maxWidth={300}>
          {!hideTotal && (
            <Grid container spacing={3}>
              <Grid item xs={4} sx={{fontWeight: 600}}>
                {t('total')}
              </Grid>

              <Grid item xs={4}>
                {totalPositive}/{totalQuestions}
              </Grid>

              <Grid
                item
                xs={4}
                sx={{
                  color: completionScoreBandColor(value)
                }}
              >
                {value}%
              </Grid>
            </Grid>
          )}

          {summaries &&
            QUESTION_DIFFICULTIES.map((questionDifficulty) => {
              return (
                <Grid container key={questionDifficulty} spacing={3}>
                  <Grid item xs={4} data-test-id={`${questionDifficulty}-rating-difficulty`}>
                    {t(`questionDifficulty.${questionDifficulty}`)}
                  </Grid>

                  <Grid item xs={4} data-test-id={`${questionDifficulty}-rating-division`}>
                    {summaries[questionDifficulty].answeredPositively}/
                    {summaries[questionDifficulty].questionCount}
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{
                      color: completionScoreBarColor(summaries, questionDifficulty)
                    }}
                    data-test-id={`${questionDifficulty}-rating-percent`}
                  >
                    {summaries[questionDifficulty].positivePercent}%
                  </Grid>
                </Grid>
              )
            })}
        </Grid>
      </Popover>
    </Typography>
  )
}
